import React from 'react';
import { X, Lock } from 'lucide-react';
import { MedalIcon } from './ui/MedalIcon';
import { Eyebrow } from './ui/Eyebrow';
import type { Achievement } from '../types/social';
import { useBodyScrollLock } from '../hooks/useBodyScrollLock';

interface Props {
  medal: Achievement | null;
  earnedAt?: string | null;
  locked: boolean;
  onClose: () => void;
}

const CATEGORY_LABELS: Record<string, string> = {
  collection: 'Coleção',
  confed: 'Confederação',
  social: 'Social',
  time: 'Tempo',
  event: 'Eventos',
  special: 'Especial',
};

function formatDate(iso: string): string {
  try {
    const d = new Date(iso);
    return d.toLocaleDateString('pt-BR', { day: '2-digit', month: 'long', year: 'numeric' });
  } catch {
    return iso;
  }
}

export function MedalDetailModal({ medal, earnedAt, locked, onClose }: Props) {
  useBodyScrollLock(medal !== null);
  if (!medal) return null;
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={medal.name}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-6"
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="relative w-full max-w-sm bg-white rounded-2xl shadow-lg px-6 py-7 pb-[calc(1.75rem+env(safe-area-inset-bottom))] my-auto max-h-[90vh] overflow-y-auto text-center animate-fade-up"
      >
        <button
          type="button"
          onClick={onClose}
          aria-label="Fechar"
          className="absolute top-3 right-3 p-2 rounded-full text-ink-500 hover:text-ink-900 hover:bg-ink-100"
        >
          <X size={18} />
        </button>
        <div className="flex items-center justify-center mb-4">
          <MedalIcon medal={medal} size={96} locked={locked} />
        </div>
        <Eyebrow color="#7A6B3F" className="mb-2">
          {CATEGORY_LABELS[medal.category] ?? medal.category}
        </Eyebrow>
        <h2 className="font-display font-bold text-h2 text-ink-900 mb-2">{medal.name}</h2>
        <p className="font-body text-small text-ink-700 mb-4">{medal.description}</p>
        {locked ? (
          <p className="inline-flex items-center gap-1.5 text-caption text-ink-500 font-semibold">
            <Lock size={12} /> Ainda bloqueada
          </p>
        ) : earnedAt ? (
          <p className="text-caption text-green-700 font-semibold">
            Conquistada em {formatDate(earnedAt)}
          </p>
        ) : null}
      </div>
    </div>
  );
}
